import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Space, Tag, Typography, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import RichTextEditor from "components/RichTextEditor/RichTextEditor";
import {
  emptyValueForKind,
  fallbackPreview,
  isCleared,
  isFieldOverridden,
  originalPreview,
  seedValueForEditing,
} from "./fieldValues";
import { uploadImage } from "./pagesApi";

const { Text, Paragraph } = Typography;

// Gloria edits from a phone: every control clears the 44px touch target.
export const CONTROL = { minHeight: 44 };

// The inline actions in a field header are link buttons, which are tiny by
// default; they still have to be tappable on a phone.
const LINK_ACTION = { padding: 0, height: "auto", minHeight: 44 };

/**
 * The committed content, presented as content: a quiet card, not a disabled
 * input. A greyed-out box reads as "broken"; this reads as "this is what the
 * site says", which is the whole point of the default state.
 */
const SURFACE = {
  background: "#fafafa",
  border: "1px solid #f0f0f0",
  borderRadius: 8,
  padding: "10px 12px",
};

/**
 * The kinds the translate button appears on: plain text the editor writes.
 *
 * Not `link` (an href is not prose), not `image` (a stored URL), not `seo` —
 * a composite would need one button per part, and the two SEO parts are short
 * enough to translate by pasting into the fields above.
 */
const ASSISTABLE_KINDS = ["text", "paragraph", "richtext"];

/** Composite kinds edit one part at a time; the whole object is still what changes. */
const partChange = (value, onChange, part) => (event) =>
  onChange({ ...value, [part]: event.target.value });

const ImageInput = ({ value, onChange, placeholder }) => {
  const [isUploading, setIsUploading] = useState(false);

  const upload = async (file) => {
    setIsUploading(true);
    try {
      const url = await uploadImage(file);
      if (!url) throw new Error("Качването не върна URL адрес");
      onChange({ ...value, url });
    } catch (error) {
      message.error(`Неуспешно качване: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
    // Handled here, so antd must not also PUT the file itself.
    return false;
  };

  return (
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      {value.url && (
        <img
          src={value.url}
          alt={value.alt || ""}
          style={{ maxWidth: "100%", maxHeight: 160, borderRadius: 8 }}
        />
      )}
      <Space wrap>
        <Upload beforeUpload={upload} showUploadList={false} accept="image/*">
          <Button icon={<UploadOutlined />} loading={isUploading} style={CONTROL}>
            {value.url ? "Смени изображението" : "Качи изображение"}
          </Button>
        </Upload>
        {value.url && (
          <Button type="link" onClick={() => onChange({ ...value, url: "" })}>
            Премахни
          </Button>
        )}
      </Space>
      <Input
        value={value.alt}
        onChange={partChange(value, onChange, "alt")}
        placeholder={placeholder || "Алтернативен текст"}
        style={CONTROL}
        maxLength={500}
      />
    </Space>
  );
};

const inputForKind = ({ field, value, onChange, placeholder }) => {
  switch (field.kind) {
    case "text":
      return (
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          style={CONTROL}
          maxLength={500}
        />
      );
    case "paragraph":
      return (
        <Input.TextArea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          autoSize={{ minRows: 3, maxRows: 12 }}
          maxLength={5000}
        />
      );
    case "richtext":
      // The editor emits "" for an empty document — storing its empty paragraph
      // would pin the field to blank instead of letting it fall back to the
      // committed content.
      return <RichTextEditor value={value || ""} onChange={onChange} placeholder={placeholder} />;
    case "link":
      return (
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Input
            value={value.label}
            onChange={partChange(value, onChange, "label")}
            placeholder={field.fallback?.label || "Текст на бутона"}
            style={CONTROL}
            maxLength={200}
          />
          <Input
            value={value.href}
            onChange={partChange(value, onChange, "href")}
            placeholder={field.fallback?.href || "Адрес (URL)"}
            style={CONTROL}
            maxLength={2000}
          />
        </Space>
      );
    case "image":
      return (
        <ImageInput value={value} onChange={onChange} placeholder={field.fallback?.alt} />
      );
    case "seo":
      return (
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Input
            value={value.title}
            onChange={partChange(value, onChange, "title")}
            placeholder={field.fallback?.title || "SEO заглавие"}
            style={CONTROL}
            maxLength={200}
          />
          <Input.TextArea
            value={value.description}
            onChange={partChange(value, onChange, "description")}
            placeholder={field.fallback?.description || "SEO описание"}
            autoSize={{ minRows: 2, maxRows: 6 }}
            maxLength={500}
          />
        </Space>
      );
    default:
      // A manifest kind this build does not know about. Showing it read-only
      // beats rendering nothing: the field stays visible and, because the form
      // sends it back unchanged, nothing is silently lost.
      return <Text type="secondary">Неподдържан тип поле „{field.kind}“</Text>;
  }
};

/**
 * The default state's body: the committed text for this field, rendered the way
 * a reader would see it. Composites show the parts that carry meaning, so the
 * editor can tell at a glance whether the default is the right one.
 */
const FallbackBody = ({ field }) => {
  const fallback = field.fallback;

  if (isCleared(field.kind, fallback)) {
    return <Text type="secondary">Няма текст в сайта</Text>;
  }

  switch (field.kind) {
    case "richtext":
      // Markup from the manifest — content committed in hpn-landing, never
      // anything a user typed — so rendering it is showing our own page.
      return (
        <div className="rich-text-preview" dangerouslySetInnerHTML={{ __html: fallback }} />
      );
    case "link":
      return (
        <Space direction="vertical" size={2} style={{ width: "100%" }}>
          <Text>{fallback.label || "Без текст"}</Text>
          <Text type="secondary" style={{ fontSize: 12, wordBreak: "break-all" }}>
            {fallback.href}
          </Text>
        </Space>
      );
    case "seo":
      return (
        <Space direction="vertical" size={2} style={{ width: "100%" }}>
          <Text>{fallback.title}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {fallback.description}
          </Text>
        </Space>
      );
    case "image":
      return (
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          {fallback.url && (
            <img
              src={fallback.url}
              alt={fallback.alt || ""}
              style={{ maxWidth: "100%", maxHeight: 160, borderRadius: 8 }}
            />
          )}
          {fallback.alt && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {fallback.alt}
            </Text>
          )}
        </Space>
      );
    default:
      return (
        <Paragraph style={{ margin: 0, whiteSpace: "pre-wrap" }}>{fallback}</Paragraph>
      );
  }
};

/**
 * One manifest field, in one of three states.
 *
 * **Default** — the field has no value of its own, so the site's committed text
 * is shown as content with an Edit button. **Editing** — the input, seeded with
 * that committed text so there is something to amend. **Overridden** — the value
 * differs from the committed text, and can be handed back to the site.
 *
 * There is deliberately no fourth state where the input sits empty and the
 * editor is expected to know that empty means "use the site's copy".
 *
 * Memoized, and paired with a stable per-path `onChange` from `PageEditor`:
 * the homepage form is 64 fields, so without both, one keystroke re-renders
 * every other field — including the rich-text editors, which are expensive and
 * can drop the caret when they remount.
 */
function FieldInput({ field, value, onChange, onAssist, startOpen, focusNonce, onFocused }) {
  const placeholder = fallbackPreview(field.kind, field.fallback);
  const [isTranslating, setIsTranslating] = useState(false);

  // A field that already holds something — a saved draft, or text typed this
  // session — is being edited by definition, so the only state worth keeping
  // locally is "opened a default field but has not typed yet". Everything else
  // is derived from `value` and the manifest, which keeps the memoization
  // honest: no new prop, no extra render.
  // `startOpen`: a list item the editor just created has no site text to show
  // in the default state, so its parts open straight into their inputs.
  const [wasOpened, setWasOpened] = useState(Boolean(startOpen));
  const isEditing = wasOpened || !isCleared(field.kind, value);
  const isOverridden = isFieldOverridden(field, value);
  const original = originalPreview(field.kind, field.fallback);

  // Absent while editing Bulgarian, which is what the button translates *from*.
  const canAssist = Boolean(onAssist) && ASSISTABLE_KINDS.includes(field.kind);

  /**
   * Enter editing with the committed text already in the input. Seeding is safe
   * because `payloadFromValues` sends anything equal to its fallback as cleared:
   * opening a field and closing it again stores nothing.
   */
  const startEditing = () => {
    setWasOpened(true);
    const seed = seedValueForEditing(field.kind, value, field.fallback);
    if (!isCleared(field.kind, seed)) onChange(seed);
  };

  /** Hand the field back to the site: cleared is what the API deletes. */
  const revert = () => {
    setWasOpened(false);
    onChange(emptyValueForKind(field.kind));
  };

  /**
   * Click-to-edit (DAB-902): a new `focusNonce` means the editor clicked this
   * field's text in the live preview. A field still showing the site's text is
   * opened first (the effect runs again once its input exists), then scrolled
   * to and focused, and `onFocused` tells the editor the request is spent.
   */
  const rootRef = useRef(null);
  const handledNonce = useRef(undefined);
  useEffect(() => {
    if (!focusNonce || handledNonce.current === focusNonce) return;
    if (!isEditing) {
      startEditing();
      return;
    }
    handledNonce.current = focusNonce;
    const root = rootRef.current;
    root?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    root
      ?.querySelector("textarea, input:not([disabled]), [contenteditable='true']")
      ?.focus({ preventScroll: true });
    onFocused?.();
    // `startEditing` is rebuilt every render; the nonce is what this reacts to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusNonce, isEditing]);

  const translate = async () => {
    setIsTranslating(true);
    try {
      const suggestion = await onAssist(field);
      if (!suggestion) throw new Error("Преводът се върна празен");
      // Filled in, never saved — the editor reviews it and presses Save.
      onChange(suggestion);
    } catch (error) {
      message.error(`Неуспешен превод: ${error?.response?.data?.message || error.message}`);
    } finally {
      setIsTranslating(false);
    }
  };

  if (field.locked) {
    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ marginBottom: 6 }}>
          <Text strong>{field.label}</Text> <Tag>Зададено в кода</Tag>
        </div>
        <Input value={placeholder} disabled style={CONTROL} />
      </div>
    );
  }

  return (
    <div ref={rootRef} style={{ marginBottom: 20 }}>
      <div
        style={{
          marginBottom: 6,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Text strong>{field.label}</Text>
        {!isEditing && <Tag color="default">От сайта</Tag>}
        {isOverridden && <Tag color="orange">Променено</Tag>}
        {isEditing ? (
          <Button type="link" size="small" style={LINK_ACTION} onClick={revert}>
            Върни текста от сайта
          </Button>
        ) : (
          <Button type="link" size="small" style={LINK_ACTION} onClick={startEditing}>
            Редактирай
          </Button>
        )}
        {isEditing && canAssist && (
          <Button
            type="link"
            size="small"
            style={LINK_ACTION}
            loading={isTranslating}
            onClick={translate}
          >
            Преведи от BG
          </Button>
        )}
      </div>

      {isEditing ? (
        <>
          {inputForKind({ field, value, onChange, placeholder })}
          {original && (
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
              Оригинал: {original}
            </Text>
          )}
        </>
      ) : (
        <div style={SURFACE}>
          <FallbackBody field={field} />
        </div>
      )}
    </div>
  );
}

export default React.memo(FieldInput);
